<template>
  <div class="">


    <b-button v-b-modal.data-import pill>
      <b-icon icon="upload" class="ml-1"></b-icon>
      {{ buttonText }}
    </b-button>

    <b-modal id="data-import" hide-footer :size="report ? 'lg' : 'md'">


      <form @submit.prevent="submit" dir="rtl" class="text-right import-form">
        <h5 class="text-center mb-2">{{ buttonText }}</h5>
        <p class="text-center small mb-4">
          <a :href="sampleFile" download>
            <b-icon icon="download" class="ml-1"/>
            دانلود فایل نمونه{{ dataType === 'product' ? ' (همراه با راهنمای ستون‌ها)' : '' }}
          </a>
        </p>

        <form-group :validations="$v.form.data">
          <div slot-scope="{ errors, invalid }">
            <div class="custom-file">
              <input @change="handleFileUpload" type="file" accept=".xlsx,.xls,.csv" class="custom-file-input" id="customFile"
                     :disabled="running">
              <label class="custom-file-label text-truncate" for="customFile">{{ fileName || 'انتخاب فایل' }}</label>
            </div>
            <form-errors-manager :errors="errors"/>
          </div>
        </form-group>

        <p v-if="fileSummary" class="small text-muted mt-2 mb-0">{{ fileSummary }}</p>

        <!-- progress: contained in the modal, one row per phase -->
        <div v-if="running" class="import-progress mt-3">
          <div v-for="phase in phases.filter(p => p.total)" :key="phase.key" class="mb-2">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ phase.title }}</span>
              <span dir="ltr"><bdi>{{ phase.done }} / {{ phase.total }}</bdi> ({{ percent(phase) }}%)</span>
            </div>
            <b-progress :max="100" height="1rem" class="w-100">
              <b-progress-bar :value="percent(phase)" :animated="phase.active" :variant="phase.failed ? 'warning' : 'primary'">
                {{ percent(phase) }}%
              </b-progress-bar>
            </b-progress>
          </div>
          <div class="d-flex flex-wrap align-items-center mt-2">
            <button v-if="dataType === 'product'" type="button" class="btn btn-sm btn-outline-secondary ml-2 mb-1"
                    @click="paused = !paused">
              <b-icon :icon="paused ? 'play-fill' : 'pause-fill'"/>
              {{ paused ? 'ادامه' : 'توقف موقت' }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger mb-1" @click="cancelSendData()">
              <b-icon icon="x"/>
              لغو
            </button>
          </div>
          <p v-if="retryMessage" class="small text-warning mt-2 mb-0">{{ retryMessage }}</p>
          <p v-if="paused" class="small text-secondary mt-2 mb-0">ارسال متوقف شده است؛ برای ادامه «ادامه» را بزنید.</p>
        </div>

        <!-- final report -->
        <div v-if="report" class="import-report mt-3">
          <div class="ir-banner" :class="'is-' + reportState">
            <span class="ir-banner-icon">
              <b-icon :icon="reportState === 'success' ? 'check-circle-fill' : (reportState === 'cancel' ? 'slash-circle-fill' : (reportState === 'fail' ? 'x-circle-fill' : 'exclamation-triangle-fill'))"/>
            </span>
            <div class="ir-banner-text">
              <b>{{ reportTitle }}</b>
              <span>{{ reportSentence }}</span>
            </div>
            <span class="ir-chip" title="مدت زمان">
              <b-icon icon="clock"/>
              {{ report.duration }}
            </span>
          </div>

          <div v-for="group in reportGroups" :key="group.key" class="ir-group">
            <div class="ir-group-title">
              <b-icon :icon="group.icon"/>
              {{ group.title }}
              <span class="ir-group-total">{{ group.total }} ردیف</span>
            </div>
            <div class="ir-stats">
              <div v-for="stat in group.stats" :key="stat.key" class="ir-stat" :class="['is-' + stat.tone, {'is-zero': !stat.value}]">
                <b-icon :icon="stat.icon" class="ir-stat-icon"/>
                <span class="ir-stat-value">{{ stat.value }}</span>
                <span class="ir-stat-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>

          <div v-if="report.failures.length" class="ir-failures">
            <div class="ir-failures-head">
              <span><b-icon icon="exclamation-octagon"/> ردیف‌های ناموفق <span class="ir-count">{{ report.failures.length }}</span></span>
            </div>
            <div class="ir-table-wrap">
              <table class="ir-table">
                <thead>
                <tr>
                  <th>برگه</th>
                  <th>ردیف</th>
                  <th>محصول / تنوع</th>
                  <th>علت</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(failure, i) in report.failures" :key="'f-' + i">
                  <td data-label="برگه"><span class="ir-sheet" :class="'is-' + failure.sheet">{{ failure.sheetName }}</span></td>
                  <td data-label="ردیف"><span class="ir-row">{{ failure.row }}</span></td>
                  <td data-label="محصول / تنوع" class="ir-name"><bdi>{{ failure.name || '-' }}</bdi></td>
                  <td data-label="علت" class="ir-reason">{{ failure.reason }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="ir-actions">
            <button v-if="report.failures.length && dataType === 'product'" type="button" class="ir-btn is-danger" @click="downloadFailedRows">
              <b-icon icon="download"/>
              دانلود ردیف‌های ناموفق برای اصلاح
            </button>
            <button type="button" class="ir-btn is-primary" @click="newImport">
              <b-icon icon="arrow-repeat"/>
              ورود فایل جدید
            </button>
            <button type="button" class="ir-btn is-ghost" @click="$bvModal.hide('data-import')">
              <b-icon icon="x"/>
              بستن
            </button>
          </div>
        </div>

        <div v-if="!report" class="mt-3 text-center">
          <button class="btn btn-primary px-5 rounded-pill" type="submit" :disabled="running">ثبت</button>
        </div>
      </form>

    </b-modal>


  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {required} from "vuelidate/lib/validators";
import FormGroup from "../../validator/FormGroup.vue";
import FormErrorsManager from "../../validator/FormErrorsManager.vue";
import {parseProductWorkbook, buildFailedRowsWorkbook, PRODUCT_SHEET, VARIANT_SHEET} from "./productExcel";

const PRODUCT_CHUNK = 50
const VARIANT_CHUNK = 200

function chunkArray(array, size) {
  const result = []
  for (let i = 0; i < array.length; i += size) {
    result.push({offset: i, rows: array.slice(i, i + size)})
  }
  return result
}

function formatDuration(ms) {
  const seconds = Math.round(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  return minutes ? `${minutes} دقیقه و ${seconds % 60} ثانیه` : `${seconds} ثانیه`
}

/** readable Persian message for a failed request */
function requestErrorMessage(error) {
  const response = error && error.response
  if (!response) return 'خطای ارتباط با سرور (اینترنت یا سرور در دسترس نیست)'
  const data = response.data || {}
  if (data.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors).reduce((all, item) => all.concat(item), [])
    if (messages.length) return messages.join('، ')
  }
  if (response.status === 429) return 'تعداد درخواست‌ها بیش از حد مجاز بود؛ دوباره تلاش کنید'
  if (response.status >= 500) return 'خطای سرور هنگام ثبت (' + response.status + ')' + (data.message ? ': ' + data.message : '')
  return data.message || ('خطای ' + response.status)
}

export default {
  name: "DataImportManager",
  components: {FormErrorsManager, FormGroup},
  props: {
    dataType: {
      default: 'user', // for now support: user, product and menu
    }
  },
  data() {
    return {
      form: {
        data: null,
        type: this.dataType,
      },
      fileName: '',
      fileSummary: '',
      running: false,
      phases: [],
      paused: false,
      retryMessage: '',
      report: null,
      cancelProcess: false
    };
  },
  created() {
    // parsed workbook is kept outside of Vue reactivity (can be thousands of rows)
    this.parsed = null
    this.failures = []
  },
  validations: {
    form: {
      data: {required},
    },
  },
  computed: {
    reportState() {
      const r = this.report
      if (!r) return ''
      if (r.cancelled) return 'cancel'
      if (!r.failures.length) return 'success'
      const ok = ['products', 'variants'].reduce((sum, key) => sum + (r[key] ? r[key].created + r[key].updated + r[key].unchanged : 0), 0) + (r.rows ? r.rows.ok : 0)
      return ok ? 'partial' : 'fail'
    },
    reportTitle() {
      return {success: 'ورود اطلاعات با موفقیت انجام شد', partial: 'ورود اطلاعات با خطا در برخی ردیف‌ها پایان یافت', fail: 'هیچ ردیفی ثبت نشد', cancel: 'عملیات لغو شد'}[this.reportState] || ''
    },
    reportSentence() {
      const r = this.report
      if (!r) return ''
      const parts = []
      if (r.products) parts.push(`${r.products.created + r.products.updated + r.products.unchanged} از ${r.products.total} محصول`)
      if (r.variants) parts.push(`${r.variants.created + r.variants.updated + r.variants.unchanged} از ${r.variants.total} تنوع`)
      if (r.rows) parts.push(`${r.rows.ok} از ${r.rows.total} ردیف`)
      return (parts.length ? parts.join(' و ') + ' با موفقیت پردازش شد' : '') + (r.failures.length ? `؛ ${r.failures.length} ردیف نیاز به اصلاح دارد.` : '.')
    },
    reportGroups() {
      const r = this.report
      if (!r) return []
      const stat = (key, label, icon, tone, value) => ({key, label, icon, tone, value: value || 0})
      const groups = []
      if (r.products) {
        groups.push({key: 'products', title: 'محصولات', icon: 'box-seam', total: r.products.total, stats: [
          stat('created', 'ایجاد', 'plus-circle', 'green', r.products.created),
          stat('updated', 'به‌روزرسانی', 'pencil-square', 'blue', r.products.updated),
          stat('unchanged', 'بدون تغییر', 'dash-circle', 'gray', r.products.unchanged),
          stat('failed', 'ناموفق', 'x-circle', 'red', r.products.failed),
        ].concat(r.products.notSent ? [stat('notSent', 'ارسال‌نشده', 'pause-circle', 'gray', r.products.notSent)] : [])})
      }
      if (r.variants) {
        groups.push({key: 'variants', title: 'تنوع‌ها', icon: 'diagram-3', total: r.variants.total, stats: [
          stat('created', 'ایجاد', 'plus-circle', 'green', r.variants.created),
          stat('updated', 'به‌روزرسانی', 'pencil-square', 'blue', r.variants.updated),
          stat('unchanged', 'بدون تغییر', 'dash-circle', 'gray', r.variants.unchanged),
          stat('skipped', 'ردشده', 'skip-forward-circle', 'amber', r.variants.skipped),
          stat('failed', 'ناموفق', 'x-circle', 'red', r.variants.failed),
        ].concat(r.variants.notSent ? [stat('notSent', 'ارسال‌نشده', 'pause-circle', 'gray', r.variants.notSent)] : [])})
        if (r.variants.createdFeatures || r.variants.createdValues) {
          groups.push({key: 'features', title: 'ویژگی‌های ساخته‌شده', icon: 'tags', total: r.variants.createdFeatures + r.variants.createdValues, stats: [
            stat('createdFeatures', 'ویژگی جدید', 'tag', 'teal', r.variants.createdFeatures),
            stat('createdValues', 'مقدار جدید', 'plus-square', 'teal', r.variants.createdValues),
          ]})
        }
      }
      if (r.rows) {
        groups.push({key: 'rows', title: 'ردیف‌ها', icon: 'list-ul', total: r.rows.total, stats: [
          stat('ok', 'موفق', 'check-circle', 'green', r.rows.ok),
          stat('failed', 'ناموفق', 'x-circle', 'red', r.rows.failed),
        ]})
      }
      return groups
    },
    buttonText() {
      switch (this.dataType) {
        case 'user':
          return 'افزودن دسته ای کاربر'
        case 'product':
          return 'افزودن دسته ای محصولات'
        case 'menu':
          return 'افزودن دسته ای منو'

        default:
          return 'یافت نشد.'
      }

    },
    sampleFile() {

      switch (this.dataType) {
        case 'user':
          return '/batch_users.xlsx'
        case 'product':
          return '/batch_products.xlsx'
        case 'menu':
          return '/batch_menus.xlsx'
        default:
          return '/'
      }

    }
  },
  methods: {
    percent(phase) {
      return phase.total ? Math.round((phase.done * 100) / phase.total) : 100
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.report = null
      this.fileSummary = ''
      this.parsed = null
      this.form.data = null
      if (!file) {
        this.fileName = ''
        return
      }
      this.fileName = file.name
      const reader = new FileReader();
      reader.onload = (e) => {
        // read + parse once; submit only uses the parsed result
        let workbook
        try {
          workbook = XLSX.read(new Uint8Array(e.target.result), {type: "array"});
        } catch (error) {
          this.fileSummary = 'فایل قابل خواندن نیست؛ یک فایل اکسل معتبر انتخاب کنید.'
          return
        }

        if (this.dataType === 'product') {
          // same format as the product export: "محصولات" sheet + optional "تنوع‌ها" sheet ("راهنما" is ignored)
          const parsed = parseProductWorkbook(XLSX, workbook)
          this.parsed = Object.freeze(parsed)
          this.form.data = parsed.products.length || parsed.variants.length ? Object.freeze(parsed.products.slice()) : null
          this.fileSummary = `${parsed.products.length} محصول و ${parsed.variants.length} تنوع در فایل پیدا شد.`
          return
        }

        // Assuming the first sheet is the one you need
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {defval: null});
        this.parsed = Object.freeze({rows, sheetName: workbook.SheetNames[0]})
        this.form.data = rows.length ? Object.freeze(rows) : null
        this.fileSummary = `${rows.length} ردیف در فایل پیدا شد.`
      };
      reader.readAsArrayBuffer(file);
    },
    invalid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    cancelSendData() {
      this.cancelProcess = true;
      this.paused = false;
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async waitWhilePaused() {
      while (this.paused && !this.cancelProcess) {
        await this.sleep(300)
      }
    },
    // send one request; on 429 (Retry-After or exponential backoff), network errors and 5xx retry automatically so no row is lost
    async sendWithRetry(part) {
      let attempt = 0
      for (; ;) {
        await this.waitWhilePaused()
        if (this.cancelProcess) {
          throw new Error('cancelled')
        }
        try {
          const result = await this.$store.dispatch('DataImport/sendData', part)
          this.retryMessage = ''
          return result
        } catch (error) {
          const status = error && error.response ? error.response.status : 0
          const retryable = status === 429 || status === 0 || status >= 500
          attempt++
          if (!retryable || attempt > 8 || this.cancelProcess) {
            throw error
          }
          let waitMs = Math.min(30000, 1000 * Math.pow(2, attempt - 1))
          const retryAfter = error.response && error.response.headers && error.response.headers['retry-after']
          if (status === 429 && retryAfter && !isNaN(Number(retryAfter))) {
            waitMs = Math.max(1000, Number(retryAfter) * 1000)
          }
          this.retryMessage = (status === 429 ? 'محدودیت تعداد درخواست' : 'خطای ارتباط') + `؛ تلاش دوباره تا ${Math.round(waitMs / 1000)} ثانیه دیگر (تلاش ${attempt})`
          await this.sleep(waitMs)
        }
      }
    },
    addFailure(sheet, meta, reason) {
      this.failures.push({
        sheet,
        sheetName: sheet === 'variant' ? VARIANT_SHEET : (sheet === 'product' ? PRODUCT_SHEET : (this.parsed && this.parsed.sheetName) || ''),
        row: meta ? meta.row : '-',
        name: meta ? meta.name : '',
        raw: meta ? meta.raw : null,
        reason,
      })
    },
    // run one phase: send chunks one by one, call handle(result, chunk) per response
    async runPhase(phase, chunks, type, handle, onError) {
      phase.active = true
      for (const chunk of chunks) {
        if (this.cancelProcess) break
        try {
          const result = await this.sendWithRetry({data: chunk.rows, type})
          handle((result && result.data && result.data.data) || {}, chunk)
        } catch (error) {
          if (this.cancelProcess) break
          onError(requestErrorMessage(error), chunk)
          phase.failed = true
        }
        phase.done += chunk.rows.length
      }
      phase.active = false
    },
    async submitProducts(products, variants, report) {
      const {productMeta, variantMeta} = this.parsed
      const [productPhase, variantPhase] = this.phases

      if (products.length) {
        const stats = report.products
        await this.runPhase(productPhase, chunkArray(products, PRODUCT_CHUNK), 'product_batch', (summary, chunk) => {
          (summary.results || []).forEach(row => {
            if (row.ok) {
              const action = row.action || 'updated'
              stats[action] = (stats[action] || 0) + 1
            } else {
              stats.failed++
              this.addFailure('product', productMeta[chunk.offset + row.row], (row.errors || []).join('، ') || 'خطای نامشخص')
            }
          })
        }, (message, chunk) => {
          stats.failed += chunk.rows.length
          chunk.rows.forEach((_, i) => this.addFailure('product', productMeta[chunk.offset + i], message))
        })
        stats.notSent = Math.max(0, stats.total - productPhase.done)
      }

      if (variants.length && !this.cancelProcess) {
        const stats = report.variants
        await this.runPhase(variantPhase, chunkArray(variants, VARIANT_CHUNK), 'product_variant', (summary, chunk) => {
          stats.created += summary.created || 0
          stats.updated += summary.updated || 0
          stats.unchanged += summary.unchanged || 0
          stats.createdFeatures += summary.created_features || 0
          stats.createdValues += summary.created_values || 0
          ;(summary.skipped || []).forEach(item => {
            stats.skipped++
            this.addFailure('variant', variantMeta[chunk.offset + Number(item.row)], item.reason || 'ردشده')
          })
        }, (message, chunk) => {
          stats.failed += chunk.rows.length
          chunk.rows.forEach((_, i) => this.addFailure('variant', variantMeta[chunk.offset + i], message))
        })
      }
      if (report.variants) {
        report.variants.notSent = Math.max(0, report.variants.total - variantPhase.done)
      }
    },
    async submitRows(rows, report) {
      const phase = this.phases[0]
      // منو نباید chunk شود تا همه آیتم‌ها در یک درخواست بروند
      const chunks = this.dataType === 'menu' ? [{offset: 0, rows}] : chunkArray(rows, 1)
      const meta = (i) => ({row: i + 2, name: rows[i] && (rows[i].name || rows[i].title || rows[i].mobile || rows[i].email) || '', raw: null})
      await this.runPhase(phase, chunks, this.dataType, (summary, chunk) => {
        report.rows.ok += chunk.rows.length
      }, (message, chunk) => {
        report.rows.failed += chunk.rows.length
        chunk.rows.forEach((_, i) => this.addFailure('rows', meta(chunk.offset + i), message))
      })
    },
    async submit() {
      if (!this.invalid() || !this.parsed || this.running) {
        return
      }
      const startedAt = Date.now()
      this.cancelProcess = false
      this.paused = false
      this.retryMessage = ''
      this.report = null
      this.failures = []

      const isProduct = this.dataType === 'product'
      const products = isProduct ? this.parsed.products : []
      const variants = isProduct ? this.parsed.variants : []
      const rows = isProduct ? [] : this.parsed.rows

      const report = {
        products: isProduct && products.length ? {total: products.length, created: 0, updated: 0, unchanged: 0, failed: 0, notSent: 0} : null,
        variants: isProduct && variants.length ? {total: variants.length, created: 0, updated: 0, unchanged: 0, skipped: 0, failed: 0, notSent: 0, createdFeatures: 0, createdValues: 0} : null,
        rows: isProduct ? null : {total: rows.length, ok: 0, failed: 0},
      }
      this.phases = isProduct
        ? [
          {key: 'products', title: 'محصولات', done: 0, total: products.length, active: false, failed: false},
          {key: 'variants', title: 'تنوع‌ها', done: 0, total: variants.length, active: false, failed: false},
        ].filter(phase => phase.total)
        : [{key: 'rows', title: 'ردیف‌ها', done: 0, total: rows.length, active: false, failed: false}]
      if (isProduct) {
        // keep fixed positions for submitProducts (products first, variants second)
        this.phases = [
          this.phases.find(p => p.key === 'products') || {key: 'products', done: 0, total: 0},
          this.phases.find(p => p.key === 'variants') || {key: 'variants', done: 0, total: 0},
        ]
      }
      this.running = true
      try {
        if (isProduct) {
          await this.submitProducts(products, variants, report)
        } else {
          await this.submitRows(rows, report)
        }
      } finally {
        this.running = false
        this.retryMessage = ''
        this.report = Object.assign(report, {
          cancelled: this.cancelProcess,
          duration: formatDuration(Date.now() - startedAt),
          failures: Object.freeze(this.failures.slice()),
        })
        this.$emit('imported')
      }
    },
    newImport() {
      this.report = null
      this.failures = []
      this.parsed = null
      this.form.data = null
      this.fileName = ''
      this.fileSummary = ''
      const input = this.$el && this.$el.ownerDocument.getElementById('customFile')
      if (input) input.value = ''
      this.$v.$reset()
    },
    downloadFailedRows() {
      if (!this.report || !this.report.failures.length || !this.parsed) return
      const workbook = buildFailedRowsWorkbook(XLSX, this.parsed, this.report.failures.filter(f => f.raw))
      const base = (this.fileName || 'products').replace(/\.[^.]+$/, '')
      XLSX.writeFile(workbook, `${base}-failed-rows.xlsx`)
    },
  }
}
</script>

<style scoped>
.import-form {
  min-width: 0;
}

.import-progress {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.import-report {
  --ir-radius: 14px;
  font-size: 13px;
}

.ir-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--ir-radius);
  border: 1px solid transparent;
  margin-bottom: 14px;
}

.ir-banner.is-success { background: #eaf7ef; border-color: #c7ead4; color: #1d6b3f; }
.ir-banner.is-partial { background: #fff6e5; border-color: #f6dfae; color: #8a5a00; }
.ir-banner.is-fail { background: #fdecec; border-color: #f5c6c6; color: #a12828; }
.ir-banner.is-cancel { background: #f1f3f5; border-color: #e1e5e9; color: #495057; }

.ir-banner-icon {
  flex: 0 0 auto;
  display: inline-flex;
  font-size: 26px;
}

.ir-banner-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ir-banner-text b { font-size: 14px; }
.ir-banner-text span { font-size: 12px; opacity: .9; }

.ir-chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .75);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.ir-group { margin-bottom: 12px; }

.ir-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  margin-bottom: 8px;
}

.ir-group-total {
  margin-right: auto;
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  background: #f4f5f7;
  border-radius: 999px;
  padding: 2px 9px;
}

.ir-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(105px, 1fr));
  gap: 8px;
}

.ir-stat {
  --tone: #495057;
  --tint: #f4f5f7;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--tint);
  color: var(--tone);
  border: 1px solid rgba(0, 0, 0, .03);
}

.ir-stat.is-green { --tone: #1d7a46; --tint: #e8f6ee; }
.ir-stat.is-blue { --tone: #1f5fa8; --tint: #e8f0fb; }
.ir-stat.is-gray { --tone: #5c636a; --tint: #f3f4f6; }
.ir-stat.is-amber { --tone: #9a6200; --tint: #fff4dc; }
.ir-stat.is-teal { --tone: #0f6d80; --tint: #e6f4f7; }
.ir-stat.is-red { --tone: #b42323; --tint: #fdeaea; }
.ir-stat.is-zero { opacity: .55; }

.ir-stat-icon { grid-row: 1 / span 2; font-size: 20px; }
.ir-stat-value { font-size: 18px; font-weight: 800; line-height: 1.1; }
.ir-stat-label { font-size: 11px; font-weight: 600; opacity: .85; }

.ir-failures { margin-top: 14px; }

.ir-failures-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  color: #a12828;
  margin-bottom: 8px;
}

.ir-count {
  display: inline-block;
  min-width: 22px;
  padding: 1px 8px;
  margin-right: 4px;
  border-radius: 999px;
  background: #fdeaea;
  text-align: center;
  font-size: 11px;
}

.ir-table-wrap {
  max-height: 320px;
  overflow: auto;
  border: 1px solid #eef0f2;
  border-radius: 12px;
}

.ir-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.ir-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8f9fa;
  color: #6c757d;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
  padding: 8px 10px;
  border-bottom: 1px solid #eef0f2;
}

.ir-table td {
  padding: 8px 10px;
  vertical-align: top;
  border-bottom: 1px solid #f3f4f6;
}

.ir-table tbody tr:nth-child(even) td { background: #fcfcfd; }
.ir-table tbody tr:hover td { background: #f6f8fb; }
.ir-table tbody tr:last-child td { border-bottom: 0; }

.ir-sheet {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: #eef1f4;
  color: #495057;
}

.ir-sheet.is-product { background: #e8f6ee; color: #1d7a46; }
.ir-sheet.is-variant { background: #e6f4f7; color: #0f6d80; }

.ir-row {
  display: inline-block;
  min-width: 28px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #1e1e1e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.ir-name { font-weight: 600; word-break: break-word; }
.ir-reason { color: #b42323; word-break: break-word; }

.ir-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
}

.ir-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.ir-btn:hover { transform: translateY(-1px); }
.ir-btn:focus { outline: none; box-shadow: 0 0 0 3px rgba(30, 30, 30, .15); }
.ir-btn.is-primary { background: #1e1e1e; color: #fff; }
.ir-btn.is-primary:hover { background: #333; }
.ir-btn.is-danger { background: #fdeaea; color: #b42323; border-color: #f5c6c6; }
.ir-btn.is-danger:hover { background: #fbd9d9; }
.ir-btn.is-danger:focus { box-shadow: 0 0 0 3px rgba(180, 35, 35, .2); }
.ir-btn.is-ghost { background: #fff; color: #495057; border-color: #e1e5e9; }
.ir-btn.is-ghost:hover { background: #f4f5f7; }

@media (max-width: 576px) {
  .ir-banner { flex-wrap: wrap; }
  .ir-chip { margin-right: 38px; }
  .ir-stats { grid-template-columns: repeat(2, 1fr); }

  .ir-table-wrap { max-height: 420px; border: 0; }
  .ir-table thead { display: none; }
  .ir-table, .ir-table tbody, .ir-table tr, .ir-table td { display: block; width: 100%; }
  .ir-table tr {
    border: 1px solid #eef0f2;
    border-radius: 12px;
    padding: 6px 10px;
    margin-bottom: 8px;
    background: #fff;
  }
  .ir-table tbody tr:nth-child(even) td, .ir-table tbody tr:hover td { background: transparent; }
  .ir-table td {
    display: flex;
    gap: 8px;
    padding: 4px 0;
    border: 0;
  }
  .ir-table td::before {
    content: attr(data-label);
    flex: 0 0 82px;
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
  }

  .ir-actions { flex-direction: column; }
  .ir-btn { width: 100%; }
}
</style>
